import React, { useEffect, useState, useCallback } from "react";

export default function DailyBonusModal({ onClose }: { onClose: () => void }) {
  const [canClaim, setCanClaim] = useState(false);
  const [bonusAmt, setBonusAmt] = useState(50);
  const [nextClaim, setNextClaim] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);

  const fetchStatus = useCallback(async () => {
    const token = localStorage.getItem("aviator-token");
    if (!token) { setLoading(false); return; }
    try {
      const r = await fetch("/api/daily-bonus-status", { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setCanClaim(data.canClaim);
      setBonusAmt(data.bonusAmt || 50);
      if (data.nextClaim) setNextClaim(new Date(data.nextClaim));
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Countdown timer
  useEffect(() => {
    if (!nextClaim) return;
    const tick = () => {
      const diff = nextClaim.getTime() - Date.now();
      if (diff <= 0) { setCanClaim(true); setCountdown(""); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(`${h}h ${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nextClaim]);

  const handleClaim = async () => {
    const token = localStorage.getItem("aviator-token");
    if (!token) return;
    setClaiming(true);
    try {
      const r = await fetch("/api/claim-daily-bonus", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
      });
      const data = await r.json();
      if (data.status) {
        setSuccess(true);
        setMessage(`🎉 KSh ${data.bonusAmt} bonus added to your account!`);
        setCanClaim(false);
        setNextClaim(new Date(Date.now() + 24 * 60 * 60 * 1000));
      } else {
        setMessage(data.message || "Could not claim bonus");
      }
    } catch { setMessage("Connection error. Try again."); }
    setClaiming(false);
  };

  const overlay: React.CSSProperties = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
    zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center"
  };
  const modal: React.CSSProperties = {
    background: "#1a1a2e", border: "1px solid #333", borderRadius: "12px",
    padding: "28px", width: "340px", textAlign: "center",
    boxShadow: "0 20px 60px rgba(0,0,0,0.6)"
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        <button onClick={onClose} style={{ position: "absolute", top: "12px", right: "16px", background: "none", border: "none", color: "#aaa", fontSize: "24px", cursor: "pointer" }}>×</button>

        <div style={{ fontSize: "48px", marginBottom: "12px" }}>🎁</div>
        <h3 style={{ color: "#fff", margin: "0 0 8px", fontSize: "20px" }}>Daily Bonus</h3>
        <p style={{ color: "#aaa", fontSize: "13px", marginBottom: "20px" }}>
          Come back every day to claim your free bonus!
        </p>

        {loading ? (
          <p style={{ color: "#aaa" }}>Loading...</p>
        ) : (
          <>
            <div style={{ background: "#0d1117", borderRadius: "10px", padding: "20px", marginBottom: "20px" }}>
              <div style={{ color: "#ff6a00", fontSize: "13px", marginBottom: "6px" }}>Today's bonus</div>
              <div style={{ color: "#fff", fontSize: "36px", fontWeight: "bold" }}>KSh {bonusAmt}</div>
              <div style={{ color: "#666", fontSize: "11px", marginTop: "6px" }}>Wager KSh {bonusAmt * 5} to unlock</div>
            </div>

            {success && (
              <div style={{ background: "rgba(76,175,80,0.1)", border: "1px solid #4caf50", borderRadius: "6px", padding: "10px", marginBottom: "16px", color: "#4caf50", fontSize: "13px" }}>
                {message}
              </div>
            )}

            {!success && message && (
              <div style={{ color: "#ff4444", fontSize: "12px", marginBottom: "16px" }}>{message}</div>
            )}

            {canClaim ? (
              <button onClick={handleClaim} disabled={claiming}
                style={{ width: "100%", padding: "14px", background: claiming ? "#555" : "#ff4500", color: "#fff", border: "none", borderRadius: "8px", cursor: claiming ? "not-allowed" : "pointer", fontWeight: "bold", fontSize: "16px" }}>
                {claiming ? "Claiming..." : "Claim KSh " + bonusAmt + " Now!"}
              </button>
            ) : (
              <div>
                <div style={{ color: "#aaa", fontSize: "13px", marginBottom: "8px" }}>Next bonus available in</div>
                <div style={{ color: "#ff6a00", fontSize: "24px", fontWeight: "bold", fontFamily: "monospace" }}>{countdown}</div>
                <div style={{ color: "#666", fontSize: "11px", marginTop: "8px" }}>Come back tomorrow!</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
